// The library (slice 06; design/12 §2, design/23): the DM's uploads across
// every table they run, one list, newest first, the kind as a badge. Upload
// with the refusal said in the server's own words; use a picture as the
// map; put a picture on the table as a hidden marker (tokens arrive in a
// later slice); delete after a confirmation. A picture in use asks again,
// naming each table, and then goes from every table and staged scene the DM
// runs (issue 1020); a table they no longer run keeps it, and nothing changes.
import { useEffect, useRef, useState } from 'react';
import { ASSET_KINDS, EMPTY_GRID, byteLimit, defaultShares, PICTURE_LIMITS, boardExtent, parseTags, snapToCells, type AssetInfo, type AssetKind, type AssetUse, type Layer, type LibrarySort } from '@dragons-table/vtt-shared';
import { deleteAsset, describeUse, forgetPicture, listAssets, setAssetTags, setTableMap, uploadPicture } from '../../assets';
import { gridKept } from '../gridKept';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { useBoardMap } from '../../board/sceneMap';
import { ConfirmDialog } from '../Dialogs';
import { sessionEnded } from '../../session';
import { TableCharactersSection } from './CharacterParts';
import { ShareDialog, SharedWithYou } from './LibraryShares';
import { BTN } from '../ui';

const btn = BTN;

export function LibraryPanel() {
  const { campaignId, onResult, table } = useShell();
  const boardMap = useBoardMap(); // the scene on this screen (issue 834)
  const { act, store, scene } = useTableState();
  const [assets, setAssets] = useState<AssetInfo[] | null>(null);
  const [kind, setKind] = useState<AssetKind>('map');
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; tone: 'ok' | 'warn' | 'no' } | null>(null);
  const [deleting, setDeleting] = useState<AssetInfo | null>(null);
  const [everywhere, setEverywhere] = useState<{ asset: AssetInfo; uses: AssetUse[] } | null>(null);
  const [sharing, setSharing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // The name the last chosen file gave (issue 1321): a new file replaces it, but never a name the DM typed.
  const derived = useRef('');
  const baseName = (f: File) => f.name.replace(/\.[^.]+$/, '');
  /** A new file: a no said about the last one goes (design/23 §3.3: a no names the reason for this file), and the name follows the file. */
  const choose = (f: File | null) => {
    setFile(f);
    setNotice((cur) => (cur?.tone === 'no' ? null : cur));
    if (!f) return;
    if (!name.trim() || name === derived.current) setName(baseName(f));
    derived.current = baseName(f);
  };

  // Tags, the filter and the sort (issue 1036): the server narrows and orders; the tags offered are every tag seen
  // in the whole library, so narrowing to one does not take the others off the list. They are kept per picture, so a
  // tag no picture carries any more leaves the list when its last picture drops it (issue 1315).
  const [tagFilter, setTagFilter] = useState('');
  const [sort, setSort] = useState<LibrarySort>('newest');
  const [tagsOf, setTagsOf] = useState<Record<string, string[]>>({});
  const learn = (list: AssetInfo[], whole: boolean) =>
    setTagsOf((cur) => ({ ...(whole ? {} : cur), ...Object.fromEntries(list.map((a) => [a.id, a.tags ?? []])) }));
  const known = [...new Set(Object.values(tagsOf).flat())].sort();
  const refresh = () => listAssets(undefined, { ...(tagFilter ? { tag: tagFilter } : {}), sort }).then((list) => { setAssets(list); learn(list, !tagFilter); });
  useEffect(() => { void refresh(); }, [tagFilter, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveTags = async (a: AssetInfo, line: string) => {
    const tags = parseTags(line.split(',').map((t) => t.trim()).filter(Boolean));
    if (!tags) { setNotice({ text: 'Up to ten tags, each 1 to 30 characters.', tone: 'no' }); return; }
    if (tags.join(',') === (a.tags ?? []).join(',')) return;
    const next = await setAssetTags(a.id, tags);
    if (!next) { if (!sessionEnded()) setNotice({ text: 'Those tags could not be saved right now.', tone: 'no' }); return; }
    // A picture that no longer carries the tag being shown leaves the list (issue 1315).
    setAssets((cur) => (cur ?? []).flatMap((x) => (x.id !== a.id ? [x] : tagFilter && !(next.tags ?? []).includes(tagFilter) ? [] : [{ ...x, tags: next.tags }])));
    learn([{ ...a, tags: next.tags }], false);
  };

  /** Put a picture on the table: a token (shown), a token on the DM layer (starts hidden), or a prop (scenery, locked). */
  const place = (a: AssetInfo, layer: Layer) => {
    const extent = boardExtent(boardMap);
    const at = snapToCells(store.state.grid, extent, { x: EMPTY_GRID.squarePx * 2.5, y: EMPTY_GRID.squarePx * 2.5 }, { w: 1, h: 1 }).centre;
    act({ kind: 'object.add', object: {
      id: crypto.randomUUID(), kind: layer === 'scenery' ? 'prop' : 'token', name: a.name, x: at.x, y: at.y, size: { w: 1, h: 1 }, fit: 'stretch',
      layer, hidden: layer === 'dm', owners: [], asset: a.id, locked: layer === 'scenery', pc: false, values: {}, shares: defaultShares(false), nameplate: true,
    } });
  };

  /** What a picture can be used for at the table: the person's own, or one shared with them (issue 1171) alike. */
  const uses = (a: AssetInfo) => (
    <>
      {/* A picture that moves is token or prop art only (issue 1176): no "Use as map". */}
      {!a.animated && <button type="button" className={btn} disabled={boardMap?.id === a.id} aria-label={`Use ${a.name} as the map`}
        onClick={async () => {
          const result = await setTableMap(campaignId, a.id, scene?.id ?? null);
          onResult(result);
          if (result.kind === 'ok') gridKept.mapChanged(store.state.grid);
        }}>
        {boardMap?.id === a.id ? 'The map now' : 'Use as map'}
      </button>}
      <button type="button" className={btn} aria-label={`Place ${a.name} on the table`} onClick={() => place(a, 'token')}>Place</button>
      <button type="button" className={btn} aria-label={`Put ${a.name} on the table, hidden`} onClick={() => place(a, 'dm')}>Place hidden</button>
      <button type="button" className={btn} aria-label={`Place ${a.name} as a prop`} onClick={() => place(a, 'scenery')}>As a prop</button>
    </>
  );

  const upload = async () => {
    // Upload with nothing chosen says so (a disabled button, or Enter in the name, said nothing at all).
    if (!file) { setNotice({ text: 'Choose a file first.', tone: 'no' }); return; }
    if (file.size > byteLimit(kind)) {
      setNotice({ text: `This file is ${Math.round(file.size / (1024 * 1024))} MB. The limit is ${Math.round(byteLimit(kind) / (1024 * 1024))} MB.`, tone: 'no' });
      return;
    }
    setBusy(true);
    const result = await uploadPicture(kind, name.trim() || baseName(file), file);
    setBusy(false);
    if (result.kind === 'ok') {
      setNotice(result.warning ? { text: result.warning, tone: 'warn' } : { text: `Added "${result.asset.name}".`, tone: 'ok' });
      setFile(null); setName(''); derived.current = '';
      if (fileInput.current) fileInput.current.value = '';
      await refresh();
    } else if (result.kind === 'refused') setNotice({ text: result.reason, tone: 'no' });
    else if (!sessionEnded()) setNotice({ text: 'The upload could not be sent right now.', tone: 'no' });
  };

  const remove = async (a: AssetInfo, all = false) => {
    const r = all ? await deleteAsset(a.id, { everywhere: true }) : await deleteAsset(a.id);
    if (r.kind === 'ok') { forgetPicture(a.id); setTagsOf(({ [a.id]: _gone, ...rest }) => rest); setNotice({ text: `Deleted "${a.name}".`, tone: 'ok' }); await refresh(); }
    else if (r.kind === 'in_use' && !all) setEverywhere({ asset: a, uses: r.uses });
    else if (r.kind === 'in_use_elsewhere') setNotice({ text: `"${a.name}" was not deleted: it is in use at ${r.tables.join(', ')}, which you no longer run.`, tone: 'no' });
    else if (!sessionEnded()) setNotice({ text: 'That could not be done right now.', tone: 'no' });
  };

  return (
    <div className="space-y-3 p-2 text-sm" data-testid="library">
      <form className="space-y-2 rounded border border-stone-700 p-2" onSubmit={(e) => { e.preventDefault(); void upload(); }}>
        <label className="block">
          <span className="text-xs text-stone-400">Picture ({'PNG, JPEG, WebP or GIF'}, up to {Math.round(PICTURE_LIMITS.bytes / (1024 * 1024))} MB, a map up to {Math.round(PICTURE_LIMITS.mapBytes / (1024 * 1024))} MB and {PICTURE_LIMITS.mapMaxPx.toLocaleString('en-US')} px a side; a token may move — animated GIF, WebP or PNG, or a WebM loop up to {PICTURE_LIMITS.videoMs / 1000} s, {Math.round(PICTURE_LIMITS.animatedBytes / (1024 * 1024))} MB)</span>
          <input ref={fileInput} type="file" aria-label="Picture" className="mt-1 block w-full text-xs" accept="image/png,image/jpeg,image/webp,image/gif,video/webm"
            onChange={(e) => choose(e.target.files?.[0] ?? null)} />
        </label>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="text-xs text-stone-400">Name</span>
            <input aria-label="Name" className="mt-1 block w-36 rounded bg-stone-800 px-2 py-1" value={name} maxLength={PICTURE_LIMITS.name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs text-stone-400">Kind</span>
            <select aria-label="Kind" className="mt-1 block rounded bg-stone-800 px-2 py-1" value={kind} onChange={(e) => setKind(e.target.value as AssetKind)}>
              {ASSET_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <button type="submit" className="vtt-btn-primary rounded bg-amber-600 px-3 py-1 font-medium text-stone-950 disabled:opacity-50" disabled={busy}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
      <div className="flex justify-end">
        <button type="button" className={btn} onClick={() => setSharing(true)} disabled={assets === null}>Share…</button>
      </div>
      <TableCharactersSection />
      {notice && (
        <p role={notice.tone === 'no' ? 'alert' : 'status'} data-testid="library-notice"
          className={notice.tone === 'no' ? 'text-amber-300' : notice.tone === 'warn' ? 'text-amber-200' : 'text-stone-300'}>
          {notice.text}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="library-find">
        <label className="flex items-center gap-1 text-stone-400">Tag
          <select aria-label="Filter by tag" className="rounded bg-stone-800 px-1 py-0.5 text-stone-100" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
            <option value="">All tags</option>
            {[...new Set([...known, ...(tagFilter ? [tagFilter] : [])])].sort().map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1 text-stone-400">Sort
          <select aria-label="Sort the library" className="rounded bg-stone-800 px-1 py-0.5 text-stone-100" value={sort} onChange={(e) => setSort(e.target.value as LibrarySort)}>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="used">Most used</option>
          </select>
        </label>
        {assets && <span className="text-stone-500" data-testid="library-count">{assets.length} {assets.length === 1 ? 'picture' : 'pictures'}</span>}
      </div>
      <ul className="divide-y divide-stone-800" data-testid="assets">
        {assets === null && <li className="py-2 text-stone-500">Loading…</li>}
        {assets !== null && assets.length === 0 && <li className="py-2 text-stone-500" data-testid="no-assets">{tagFilter ? `No picture is tagged "${tagFilter}".` : 'Nothing uploaded yet.'}</li>}
        {(assets ?? []).map((a) => (
          <li key={a.id} className="py-2 space-y-1" data-testid={`asset-${a.id}`} data-name={a.name}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-stone-800 px-1.5 text-xs uppercase tracking-wide text-stone-400">{a.kind}</span>
              <span className="font-medium">{a.name}</span>
              <span className="text-xs text-stone-500">{a.width} × {a.height} · {a.format} · {Math.max(1, Math.round(a.bytes / 1024))} KB{a.used ? ` · used ${a.used}×` : ''}</span>
            </div>
            <TagLine a={a} onSave={(line) => { void saveTags(a, line); }} />
            <div className="flex flex-wrap gap-1">
              {uses(a)}
              <button type="button" className={btn} aria-label={`Delete ${a.name}`} onClick={() => setDeleting(a)}>Delete</button>
            </div>
          </li>
        ))}
      </ul>
      <SharedWithYou actions={uses} onPlay={(t, channel) => act({ kind: 'audio.play', channel, tracks: [t.id], loop: channel === 'ambience' })} />
      {sharing && <ShareDialog pictures={assets ?? []} onClose={() => setSharing(false)} />}
      {deleting && (
        <ConfirmDialog title={`Delete "${deleting.name}"?`} confirmLabel="Delete picture" destructive
          body={<p>A deleted picture cannot be brought back. If it is on a board, you are asked again before it is taken off.</p>}
          onConfirm={() => { const a = deleting; setDeleting(null); void remove(a); }} onClose={() => setDeleting(null)} />
      )}
      {everywhere && (
        <ConfirmDialog title="Delete this picture everywhere?" confirmLabel="Delete everywhere" destructive
          body={(
            <div className="space-y-2">
              <p>"{everywhere.asset.name}" is in use. Every token wearing it, and any map it is, goes from each table you run, staged scenes too:</p>
              <ul className="list-disc pl-5" data-testid="delete-everywhere-uses">{everywhere.uses.map((u) => <li key={u.campaignId}>{describeUse(u)}</li>)}</ul>
              <p>A deleted picture cannot be brought back.</p>
            </div>
          )}
          onConfirm={() => { const a = everywhere.asset; setEverywhere(null); void remove(a, true); }} onClose={() => setEverywhere(null)} />
      )}
    </div>
  );
}

/** A picture's tags as chips and as one comma-separated line to edit (issue 1036); saved on Enter or on leaving it. */
function TagLine({ a, onSave }: { a: AssetInfo; onSave: (line: string) => void }) {
  const now = (a.tags ?? []).join(', ');
  const [line, setLine] = useState(now);
  useEffect(() => setLine(now), [now]);
  return (
    <div className="flex flex-wrap items-center gap-1 text-xs">
      <span className="flex flex-wrap gap-1" data-testid="picture-tags">
        {(a.tags ?? []).map((t) => <span key={t} className="rounded-full bg-stone-800 px-2 text-stone-300">{t}</span>)}
      </span>
      <input aria-label={`Tags of ${a.name}`} className="w-40 rounded bg-stone-800 px-1 py-0.5" placeholder="tags, comma separated" value={line}
        onChange={(e) => setLine(e.target.value)} onBlur={() => onSave(line)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSave(line); } }} />
    </div>
  );
}
